/**
 * Non-streaming REST adapters that satisfy the `Transport` interface from
 * `@cowbell/moo`. Wraps V1's existing axios-based ServiceHandlers so the
 * library uses the same backend endpoints V1 hits.
 *
 * Out of scope (throws): renameConversation (V1 has no rename UI or
 * endpoint — see slice 2b design doc). Out of scope (no-op):
 * executeAction — V1 cards keep calling V1's `useActionExecution` hook
 * directly; the V2 transport's executeAction is a placeholder until the
 * library-side action cards adopt it.
 */
import type {
  A2UIEnvelope,
  Action,
  Context,
  SlashCommand,
  Source,
  SourcesSummary,
  Transport,
  UploadedFile,
  FeedbackRating,
} from '@cowbell/moo';
import {
  deleteConversation as deleteConversationApi,
  getAgents,
  getAgentStarters,
  getConversation as getConversationApi,
  getConversations,
  getInvokableSkills,
  improvePrompt as improvePromptApi,
  suggestNextPrompt as suggestNextPromptApi,
  submitFeedback as submitFeedbackApi,
} from '../api';
import { uploadFile as uploadFileApi } from '../api/file-upload.api';
import type {
  Action as V1Action,
  Agent as V1Agent,
  ChatContext as V1ChatContext,
  ChatMessage as V1ChatMessage,
  Conversation as V1Conversation,
  ConversationSummary as V1ConversationSummary,
  FileUploadResponse,
  Skill as V1Skill,
  Source as V1Source,
  SourcesSummary as V1SourcesSummary,
} from '../types';
import { getData } from '../../../utils/functional/fp';
import { retrieveAuthTokens } from '../../../utils/next/auth.utils';
import { historyToUIMessages } from './history-to-ui-messages';
import { sortAgentsByLifecycle } from './agent-order';

/** Translates a library `Context` (`kind` discriminator) to V1's `ChatContext`
 *  (`type` discriminator). V1's wire payload uses the same field names for
 *  the variant fields, so the mapping is just a discriminator rename. */
function toV1Context(c: Context): V1ChatContext {
  const { kind, ...rest } = c;
  return { type: kind, ...rest } as V1ChatContext;
}

const STRIPPED_UUID_RE = /^[0-9a-f]{32}$/i;
const DASHED_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Restore dashes to a 32-char hex UUID. The chat stream (or AI SDK's id
 *  transport) hands us the assistant messageId in stripped form
 *  (`c4792ad8dc9e4a7e8edaac1307a71e47`) but the backend's
 *  `/messages/:id/feedback` lookup is keyed on the canonical dashed form
 *  — sending the stripped form 404s. Already-dashed ids and anything that
 *  doesn't look like a UUID pass through unchanged. */
function toCanonicalUuid(id: string): string {
  if (!id || DASHED_UUID_RE.test(id)) return id;
  if (!STRIPPED_UUID_RE.test(id)) return id;
  return [
    id.slice(0, 8),
    id.slice(8, 12),
    id.slice(12, 16),
    id.slice(16, 20),
    id.slice(20, 32),
  ].join('-');
}

function toLibraryAgent(a: V1Agent): {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category?: string;
} {
  // V1's `agent.icon` is a sprite key (e.g. 'claims', 'concierge'), not a
  // URL. Forward it as `icon` so the host's `renderAgentAvatar` slot can
  // resolve it to the bundled SVG component. `iconUrl` is left unset; the
  // library only falls back to it when the host hasn't provided a render
  // slot.
  return {
    id: a.id,
    name: a.name,
    description: a.description,
    icon: a.icon,
    category: a.category,
  };
}

/** Upload a file via V1's `/api/chat-agent/v1/files/upload`. The V1
 *  service returns a `FileUploadResponse` keyed by `fileName/fileType/
 *  fileSize/url`; the library-facing `UploadedFile` uses
 *  `id/name/mimeType/size`. `url` is the opaque upload reference V1
 *  rounds-trips on the wire — we re-emit it as `id` so downstream
 *  transports that build the chat request from `DocumentContext.url`
 *  see the same value either way. */
async function uploadFileViaV1(
  file: File,
  signal: AbortSignal | undefined,
  conversationId: string | null | undefined
): Promise<UploadedFile> {
  const form = new FormData();
  form.append('file', file);
  // V1 parity: tell the chat-agent upload endpoint to run text extraction
  // server-side. Omitting this would let the backend default to skipping
  // extraction, which silently degrades document-context utility.
  form.append('extractText', 'true');
  // V1 parity: bind the upload to the active conversation when one is
  // open. The backend uses this to attribute the file to the thread for
  // both context lookups and retention. Skipped on fresh chats.
  if (conversationId) {
    form.append('conversationId', conversationId);
  }
  const opts: { signal?: AbortSignal } = signal ? { signal } : {};
  const response: FileUploadResponse = await uploadFileApi({
    data: form,
    ...opts,
  }).then(getData);
  return {
    id: response.url,
    name: response.fileName,
    mimeType: response.fileType,
    size: response.fileSize,
    extractedText: response.extractedContent,
    s3Path: response.s3Path,
    conversationId: response.conversationId,
  };
}

function toServerHistoryMessage(m: V1ChatMessage): {
  role: 'user' | 'assistant';
  content: string;
  messageId?: string;
  correlationId?: string;
  feedbackRating?: FeedbackRating | null;
  actions?: Action[];
  sources?: Source[];
  sourcesSummary?: SourcesSummary | null;
  a2uiMessages?: A2UIEnvelope[];
  reaction?: string | null;
} {
  if (m.role === 'user') {
    return {
      role: 'user',
      content: m.content,
      messageId: m.messageId,
      ...(m.reaction ? { reaction: m.reaction } : {}),
    };
  }
  // V1 and V2 Action / Source / SourcesSummary types are structurally
  // identical (the V2 library was ported from V1 1:1), so the cast is
  // safe — see slice 2c spec, "Decision log" entry for type duplication.
  return {
    role: 'assistant',
    content: m.content,
    messageId: m.messageId,
    // V1 persists correlationId on each assistant message; carry it into
    // the data-done part so action cards on historical messages have it
    // available for executeAction round-trips (V1 parity).
    correlationId: m.correlationId,
    feedbackRating: m.feedbackRating,
    actions: m.actions as V1Action[] | undefined as Action[] | undefined,
    sources: m.sources as V1Source[] | undefined as Source[] | undefined,
    sourcesSummary: m.sourcesSummary as
      | (V1SourcesSummary & SourcesSummary)
      | null
      | undefined,
    a2uiMessages: (m as V1ChatMessage & { a2uiMessages?: A2UIEnvelope[] })
      .a2uiMessages,
  };
}

export function makeCowbellTransportRest(): Omit<Transport, 'chat'> {
  return {
    listAgents: async () => {
      const r = await getAgents().then(getData);
      // Moo first, then the policy lifecycle; the drawer lists in this order.
      return sortAgentsByLifecycle(r?.agents ?? []).map(toLibraryAgent);
    },

    listConversations: async (args = {}) => {
      // V1's /conversations is unscoped — it returns the caller's
      // conversations across all agents. `args.agentId` is currently a hint
      // we ignore (no server-side filtering); the drawer renders everything
      // grouped by `ref.agentId` from the response.
      const r = await getConversations().then(getData);
      const conversations = (r?.conversations ?? []).map(
        (c: V1ConversationSummary) => ({
          id: c.id,
          title: c.preview,
          updatedAt: c.updatedAt,
          agentId: c.agentId,
        })
      );
      return { conversations, nextCursor: null };
    },

    getConversation: async (id: string) => {
      const r: V1Conversation = await getConversationApi(id).then(getData);
      return {
        id: r.id,
        title: r.preview,
        messages: historyToUIMessages(
          (r.messages ?? []).map(toServerHistoryMessage)
        ),
      };
    },

    renameConversation: async () => {
      throw new Error(
        'renameConversation is not supported in slice 2a (no V1 endpoint).'
      );
    },

    deleteConversation: async (id: string) => {
      await deleteConversationApi(id).then(getData);
    },

    getAgent: async (id: string) => ({ id, name: '' }),

    listStarters: async (input) => {
      // V1's /starters endpoint takes context as query params. Only forward
      // defined values so the request stays minimal.
      const params: Record<string, string> = {};
      if (input.view) params.view = input.view;
      if (input.subTab) params.subTab = input.subTab;
      if (input.entityType) params.entityType = input.entityType;
      if (input.entityId) params.entityId = input.entityId;
      if (input.entityName) params.entityName = input.entityName;
      try {
        const r = await getAgentStarters(input.agentId, { params }).then(
          getData
        );
        return { starters: r?.starters ?? [] };
      } catch (err) {
        // V1 treats 404 as "no starters configured for this agent" — not an
        // error. Mirror that so the empty state silently falls back.
        const status = (err as { response?: { status?: number } } | undefined)
          ?.response?.status;
        if (status === 404) return { starters: [] };
        throw err;
      }
    },

    getSlashCommands: async (agentId: string): Promise<SlashCommand[]> => {
      try {
        const r = await getInvokableSkills({
          params: { agent_id: agentId, invokable: true },
        }).then(getData);
        return (r?.skills ?? []).map(
          (s: V1Skill): SlashCommand => ({
            name: s.commandName ?? s.id,
            description: s.commandDescription ?? s.description,
            skillId: s.id,
            ...(s.argHint !== undefined ? { argHint: s.argHint } : {}),
          })
        );
      } catch (err) {
        // Mirror listStarters above: V1 treats 404 as "no invokable skills
        // configured for this agent" — not an error. Without this, an agent
        // with none would surface a failed query instead of an empty
        // slash-command list.
        const status = (err as { response?: { status?: number } } | undefined)
          ?.response?.status;
        if (status === 404) return [];
        throw err;
      }
    },

    uploadFile: async (file, opts) =>
      uploadFileViaV1(file, opts?.signal, opts?.conversationId),

    submitFeedback: async ({ messageId, rating, comment }) => {
      await submitFeedbackApi(toCanonicalUuid(messageId), {
        data: { type: rating, comment },
      }).then(getData);
    },

    executeAction: async () => ({ status: 'ok' as const }),

    // A2UI surface gestures (e.g. EmailComposer "Send") POST through the
    // chat-agent's hybrid dispatch endpoint. The BE routes by surfaceType +
    // action; the FE only needs the `ok` bit so the surface can flip its
    // optimistic UI on/off. Error bodies are deliberately ignored here —
    // higher layers surface failures via the returned flag, matching the
    // plain-fetch pattern used elsewhere in this transport.
    executeSurfaceAction: async ({
      correlationId,
      messageId,
      surfaceId,
      surfaceType,
      sourceComponentId,
      action,
      payload,
    }) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const { accessToken } = retrieveAuthTokens();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      try {
        const res = await fetch('/api/chat-agent/v1/surfaces/action', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            correlationId,
            messageId,
            surfaceId,
            surfaceType,
            sourceComponentId,
            action,
            payload,
          }),
        });
        // The endpoint returns HTTP 200 with `{ok: <handler-success>, message}`
        // even for logical failures (e.g. OBO mint failed → email never sent),
        // so HTTP status alone is misleading. Read the body and trust its ok
        // flag; `message` is the user-facing text the FE renders as a synthetic
        // assistant turn so the user sees feedback in the chat window without
        // needing a refetch.
        if (!res.ok) return { ok: false };
        try {
          const body = (await res.json()) as {
            ok?: unknown;
            message?: unknown;
            data?: unknown;
            a2uiMessages?: unknown;
          };
          return {
            ok: body.ok === true,
            message:
              typeof body.message === 'string' ? body.message : undefined,
            data:
              body.data && typeof body.data === 'object'
                ? (body.data as Record<string, unknown>)
                : undefined,
            a2uiMessages: Array.isArray(body.a2uiMessages)
              ? (body.a2uiMessages as A2UIEnvelope[])
              : undefined,
          };
        } catch {
          return { ok: true };
        }
      } catch {
        // Network-level failures (offline, CORS, DNS) reject the fetch
        // promise rather than resolving with res.ok=false. Coerce both
        // shapes to the same { ok: false } so onA2UIAction can show the
        // failure snackbar without dealing with an unhandled rejection.
        return { ok: false };
      }
    },

    improvePrompt: async ({ prompt, agentId, contexts }) => {
      const { improvedPrompt } = await improvePromptApi({
        draft: prompt,
        agentId,
        contexts: contexts.map(toV1Context),
      });
      return improvedPrompt;
    },

    // Any failure is "no suggestion" — including a 404 from a chat-agent that
    // does not have the endpoint yet. The guess is never worth an error.
    suggestNextPrompt: async (
      { conversationId, userMessage, assistantText },
      signal
    ) => {
      try {
        const { suggestion } = await suggestNextPromptApi(
          conversationId,
          { userMessage, assistantText },
          signal
        );
        const text = typeof suggestion === 'string' ? suggestion.trim() : '';
        return text || null;
      } catch {
        return null;
      }
    },
  };
}
